import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "filled" | "filled-lake" | "tinted" | "plain" | "destructive" | "glass";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  icon?: ReactNode;
}

const cx = (...parts: Array<string | false | undefined>) => parts.filter(Boolean).join(" ");

/** Design `Button`: capsule, >= 44px. Label is provided by the caller (sentence case, verb first). */
export function Button({
  variant = "filled",
  icon,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx("ds-btn", `ds-btn--${variant}`, variant === "glass" && "ds-glass", className)}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Required: icon-only controls need an accessible name. */
  "aria-label": string;
  icon: ReactNode;
  /** `clear` glass for map controls (icons only, never text); `plain` for in-panel buttons. */
  tone?: "clear-glass" | "plain";
}

export function IconButton({
  icon,
  tone = "clear-glass",
  className,
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        "ds-btn ds-btn--icon",
        tone === "clear-glass" ? "ds-btn--glass ds-glass ds-glass--clear" : "ds-btn--plain",
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  );
}
